import {
  PointCloudCapabilityProvider,
  convertConnectModelAlignmentToViewerTransform,
  TrimBimCapabilityProvider,
  TrajectoryMetadataProvider,
} from '@trimble/web3d-providers';
import { AlignmentApiService } from '@data-access/geospatial/alignments';
import { ScanDataApiService } from '@data-access/geospatial/scan-data';
import { loadTrajectory } from '../content-loaders/trajectory';
import {
  PointCloudItem,
  TrajectoryItem,
  FileFolderItem,
  ProjectDataState,
} from './DataStateModels';
import {
  LoadItemOptions,
  LoadState,
  ProviderHandlerConfig,
  setItemLoadState,
} from '@trimble/data-loading';
import { TcTcpsApiService } from '@data-access/connect/tc-tcps';
import {
  loadPointCloud,
  resolvePointCloudGeoWithoutViewer,
} from '../content-loaders/point-cloud';
import { CrsResolver, type CoordinatesClient } from '@data-access/coordinates';
import { TcProject, TcRegion } from '@data-access/connect/tc-tcps';
import { AlignmentSettings, FormattingSettings } from '@trimble/types';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { Point2D } from '@trimble/utilities';
import { t } from '@trimble/i18n';
import { getLoadedSectionsForMissionPath } from './DataContextProviderHelpers';
import { batch } from 'solid-js';
import { Web3DViewer } from '@technology/web3d';
import { areCsibsEquivalentByIdentity } from './csib-equivalence';
import { CrsIssueKind } from '@trimble/project-data-panel-types';
import { PointCloudConfig } from './ProviderConfigurations.types';

const setPointCloudItemLoadState =
  setItemLoadState<ProjectDataState>('pointCloud');

async function recordCrsMismatchIfNeeded(
  pointCloudCsib: string,
  projectCsib: string,
  onMismatch: () => void,
  coordinatesClient: CoordinatesClient,
  signal?: AbortSignal
): Promise<void> {
  const equivalent = await areCsibsEquivalentByIdentity(
    pointCloudCsib,
    projectCsib,
    coordinatesClient
  );
  if (signal?.aborted) {
    return;
  }
  if (equivalent === false) {
    onMismatch();
  }
}

// Configuration for PointCloud provider
export const createPointCloudConfig = (
  scanDataApi: ScanDataApiService,
  crsResolver: CrsResolver,
  getRegion: () => TcRegion | undefined,
  getProject: () => TcProject | undefined,
  /**
   * Writes the loaded point cloud's csib into a first-class state-store signal
   * (`undefined` to clear on unload). Single source of truth for the 2D
   * tile-map coordinator and EmptyViewerState — keeps the data flow
   * unidirectional from load-flow down to consumers.
   */
  setLoadedCsib: (itemId: string, csib: string | undefined) => void,
  /**
   * Writes the loaded point cloud's world-coordinate center (from its bounding
   * box) into a first-class state-store signal (`undefined` to clear on
   * unload). Read by the 2D tile-map coordinator to position the basemap so
   * the 2D pane does not have to load a hidden Potree model just to derive
   * a center.
   */
  setLoadedCenter: (itemId: string, center: Point2D | undefined) => void,
  /**
   * Writes a per-cloud CRS issue for tree-node status (`undefined` to clear).
   */
  setLoadedCrsIssue: (itemId: string, issue: CrsIssueKind | undefined) => void,
  reportLoadError: () => void,
  coordinatesClient: CoordinatesClient
): PointCloudConfig => {
  const requireLoadContext = (): {
    region: TcRegion;
    project: TcProject;
  } => {
    const region = getRegion();
    if (!region) {
      throw new Error('No Region available for point cloud loading');
    }
    const project = getProject();
    if (!project) {
      throw new Error('No Project available for point cloud loading');
    }
    return { region, project };
  };

  // The CSIB identity lookup outlives loadItem, and the load controller is
  // dropped without being aborted once the load resolves. Without a lifetime of
  // its own, a lookup that resolves after an unload or a reload would write a
  // stale mismatch onto a cloud that no longer has one. A project switch clears
  // the CRS state without unloading, so its owner must abort every check.
  const mismatchChecks = new Map<string, () => void>();

  const abortMismatchCheck = (itemId: string): void => {
    mismatchChecks.get(itemId)?.();
    mismatchChecks.delete(itemId);
  };

  const abortAllMismatchChecks = (): void => {
    for (const cancel of mismatchChecks.values()) {
      cancel();
    }
    mismatchChecks.clear();
  };

  const startMismatchCheck = (
    itemId: string,
    csib: string,
    projectCsib: string,
    loadSignal?: AbortSignal
  ): void => {
    const controller = new AbortController();
    const onLoadAbort = (): void => {
      controller.abort();
    };
    const detach = (): void => {
      loadSignal?.removeEventListener('abort', onLoadAbort);
    };
    const cancel = (): void => {
      controller.abort();
      detach();
    };
    mismatchChecks.set(itemId, cancel);
    loadSignal?.addEventListener('abort', onLoadAbort, { once: true });
    void recordCrsMismatchIfNeeded(
      csib,
      projectCsib,
      () => setLoadedCrsIssue(itemId, 'mismatch'),
      coordinatesClient,
      controller.signal
    ).finally(() => {
      detach();
      if (mismatchChecks.get(itemId) === cancel) {
        mismatchChecks.delete(itemId);
      }
    });
  };

  // loadPointCloud does not check the signal after the viewer load, and a
  // project switch does not cancel loads, so a late result must not publish.
  const writeLoadedGeo = (
    itemId: string,
    csib: string | undefined,
    center: Point2D | undefined,
    project: TcProject,
    signal?: AbortSignal
  ): void => {
    if (signal?.aborted || getProject()?.id !== project.id) {
      return;
    }
    const projectCsib = project.crs?.csib64?.trim();
    // Atomic write: csib and center are paired dataset-level state. Without
    // batch the coordinator's geoReference memo would see {newCsib, oldCenter}
    // as an intermediate value, triggering a wasted full rebuild with the
    // wrong position before the center catches up. Batching keeps dataset
    // switch a single reactive transition.
    // A cloud without a CSIB cannot be georeferenced at all, so the status does
    // not depend on whether the project defines a CRS.
    const crsIssue: CrsIssueKind | undefined = csib ? undefined : 'missing';
    abortMismatchCheck(itemId);
    batch(() => {
      setLoadedCsib(itemId, csib);
      setLoadedCenter(itemId, center);
      setLoadedCrsIssue(itemId, crsIssue);
    });
    if (projectCsib && csib) {
      startMismatchCheck(itemId, csib, projectCsib, signal);
    }
  };

  return {
    abortAllMismatchChecks,

    isProviderType: (provider): provider is PointCloudCapabilityProvider =>
      provider instanceof PointCloudCapabilityProvider,

    getLoadableItems: (loadableData: ProjectDataState) =>
      loadableData.pointCloud,

    loadItem: async (
      provider: PointCloudCapabilityProvider,
      item: PointCloudItem,
      options: LoadItemOptions
    ) => {
      const { region, project } = requireLoadContext();
      const { csib, center } = await loadPointCloud(
        {
          selectedRegion: region,
          selectedProjectId: project.id,
          selectedPointCloudId: item.id,
        },
        scanDataApi,
        crsResolver,
        provider,
        options.reload ?? false,
        options.signal
      );
      writeLoadedGeo(item.id, csib, center, project, options.signal);
    },

    // Map-only layouts never register a PointCloudCapabilityProvider, so
    // loadItem does not run. Resolve CSIB + tileset center from the API.
    onLoadWithoutProviders: async (item, options) => {
      const { region, project } = requireLoadContext();
      const { csib, center } = await resolvePointCloudGeoWithoutViewer(
        {
          selectedRegion: region,
          selectedProjectId: project.id,
          selectedPointCloudId: item.id,
        },
        scanDataApi,
        crsResolver,
        options.signal
      );
      writeLoadedGeo(item.id, csib, center, project, options.signal);
    },

    unloadItem: async (
      provider: PointCloudCapabilityProvider,
      item: PointCloudItem
    ) => {
      await provider.unloadPointCloud(item.id);
    },

    // Only a user unload reaches NotLoaded. The shared onItemFullyUnloaded hook
    // also fires when the last 3D pane unregisters its provider, which would
    // drop the CSIB and center the 2D panes still need (NUMARK-1174).
    setLoadState: (setDataState, itemId, loadState) => {
      setPointCloudItemLoadState(setDataState, itemId, loadState);
      if (loadState === LoadState.NotLoaded) {
        abortMismatchCheck(itemId);
        batch(() => {
          setLoadedCsib(itemId, undefined);
          setLoadedCenter(itemId, undefined);
          setLoadedCrsIssue(itemId, undefined);
        });
      }
    },

    onLoadError: () => {
      reportLoadError();
    },
  };
};

// Configuration for TrimBim provider
export const createTrimBimConfig = (
  alignmentApi: AlignmentApiService,
  getTenantTrn: () => string | undefined,
  formattingSettings: () => FormattingSettings,
  getTrajectoryMetadataProvider: (
    viewer: Web3DViewer
  ) => TrajectoryMetadataProvider | undefined,
  getAllTrajectoryMetadataProviders: () => TrajectoryMetadataProvider[],
  getAlignmentSettings: () => AlignmentSettings,
  resolveSplittable: (
    id: string,
    signal?: AbortSignal
  ) => Promise<TrajectoryItem | undefined>
): ProviderHandlerConfig<
  TrimBimCapabilityProvider,
  TrajectoryItem,
  ProjectDataState
> => ({
  isProviderType: (provider): provider is TrimBimCapabilityProvider =>
    provider instanceof TrimBimCapabilityProvider,

  getLoadableItems: (loadableData: ProjectDataState) => loadableData.trajectory,

  loadItem: async (
    provider: TrimBimCapabilityProvider,
    item: TrajectoryItem,
    options: LoadItemOptions
  ) => {
    const tenantTrn = getTenantTrn();
    if (!tenantTrn) {
      throw new Error('No tenant TRN available for trajectory loading');
    }
    const trajectoryMetadataProvider = getTrajectoryMetadataProvider(
      provider.viewer
    );
    if (!trajectoryMetadataProvider) {
      throw new Error(
        'No TrajectoryMetadataProvider available for trajectory loading'
      );
    }
    await loadTrajectory(
      alignmentApi,
      tenantTrn,
      item,
      formattingSettings(),
      provider,
      trajectoryMetadataProvider,
      getAllTrajectoryMetadataProviders(),
      options.reload ?? false,
      options.signal,
      getAlignmentSettings(),
      resolveSplittable
    );

    syncMetadataToAllProviders(
      trajectoryMetadataProvider,
      getAllTrajectoryMetadataProviders(),
      item.id
    );
  },

  unloadItem: async (
    provider: TrimBimCapabilityProvider,
    item: TrajectoryItem
  ) => {
    await provider.unloadModel(item.id);
    // Only clear metadata for this pane — clearing every pane would drop
    // per-pane color overrides when another pane unregisters.
    getTrajectoryMetadataProvider(provider.viewer)?.removeTrajectory(item.id);
  },

  setLoadState: setItemLoadState<ProjectDataState>('trajectory'),

  onLoadError: () => {
    NotificationEventBus.emit({
      type: 'error',
      message: t('mobileMapping.projectData.trajectoryLoadFailed'),
    });
  },
});

// Configuration for Model provider
export const createModelConfig = (
  tcTcpsApi: TcTcpsApiService,
  getRegion: () => TcRegion | undefined,
  getTrajectoryMetadataProvider: (
    viewer: Web3DViewer
  ) => TrajectoryMetadataProvider | undefined,
  getAllTrajectoryMetadataProviders: () => TrajectoryMetadataProvider[]
): ProviderHandlerConfig<
  TrimBimCapabilityProvider,
  FileFolderItem,
  ProjectDataState
> => ({
  isProviderType: (provider): provider is TrimBimCapabilityProvider =>
    provider instanceof TrimBimCapabilityProvider,

  getLoadableItems: (loadableData: ProjectDataState) =>
    loadableData.tcFileFolder,

  loadItem: async (
    provider: TrimBimCapabilityProvider,
    item: FileFolderItem,
    options: LoadItemOptions
  ) => {
    const region = getRegion();
    if (!region) {
      throw new Error('No tenant Region available for model loading');
    }

    const [trimBimData, alignment] = await Promise.all([
      tcTcpsApi.getTrimBimDisplayModelData(
        region,
        item.id,
        item.versionId,
        options.signal
      ),
      tcTcpsApi.getFileAlignment(region, item.id, item.versionId),
    ]);

    options.signal.throwIfAborted();

    if (!trimBimData || trimBimData.byteLength === 0) {
      throw new Error(`Model TrimBIM data is empty for item: ${item.id}`);
    }

    const transform = convertConnectModelAlignmentToViewerTransform(alignment);

    const trajectoryMetadataProvider = getTrajectoryMetadataProvider(
      provider.viewer
    );
    if (!trajectoryMetadataProvider) {
      throw new Error(
        'No TrajectoryMetadataProvider available for model loading'
      );
    }

    await Promise.all([
      provider.loadModel(
        item.id,
        trimBimData,
        false,
        options.reload ?? false,
        transform
      ),
      trajectoryMetadataProvider.analyzeAndStore(
        item.id,
        undefined,
        trimBimData
      ),
    ]);

    syncMetadataToAllProviders(
      trajectoryMetadataProvider,
      getAllTrajectoryMetadataProviders(),
      item.id
    );
  },

  unloadItem: async (
    provider: TrimBimCapabilityProvider,
    item: FileFolderItem
  ) => {
    await provider.unloadModel(item.id);
    getTrajectoryMetadataProvider(provider.viewer)?.removeTrajectory(item.id);
  },

  setLoadState: setItemLoadState<ProjectDataState>('tcFileFolder'),

  onLoadError: () => {
    NotificationEventBus.emit({
      type: 'error',
      message: t('mobileMapping.projectData.modelLoadFailed'),
    });
  },
});

// Configuration for Styled Trajectory handler.
// Operates on the same trajectory items but handles the transform pipeline:
// get buffer → unload model → load transformed buffer → restore visuals.
// Uses the trajectory load state so spinners show on the trajectory items.
export const createStyledTrajectoryConfig = (
  formattingSettings: () => FormattingSettings,
  getTrajectoryMetadataProvider: (
    viewer: Web3DViewer
  ) => TrajectoryMetadataProvider | undefined,
  getAllTrajectoryMetadataProviders: () => TrajectoryMetadataProvider[],
  getAlignmentSettings: () => AlignmentSettings,
  dataState: ProjectDataState
): ProviderHandlerConfig<
  TrimBimCapabilityProvider,
  TrajectoryItem,
  ProjectDataState
> => ({
  isProviderType: (provider): provider is TrimBimCapabilityProvider =>
    provider instanceof TrimBimCapabilityProvider,

  getLoadableItems: (loadableData: ProjectDataState) => loadableData.trajectory,

  loadItem: async (
    provider: TrimBimCapabilityProvider,
    item: TrajectoryItem,
    options: LoadItemOptions
  ) => {
    const metadataProvider = getTrajectoryMetadataProvider(provider.viewer);
    if (!metadataProvider) return;

    const metadata = metadataProvider.getById(item.id);
    if (!metadata?.isAlignment) return;

    const settings = getAlignmentSettings();
    const decimals = formattingSettings().lengthDecimals ?? 0;

    const buffer = metadataProvider.getTransformedBuffer(
      item.id,
      settings,
      decimals
    );
    if (!buffer) return;

    options.signal.throwIfAborted();

    // Prepare every pane's tool BEFORE unload so the AlignmentNavigationTool
    // does not exit navigation when the model is removed. Only the reloading
    // pane invalidates its materials signature. Then capture the active
    // pane's navigation state (any pane may hold it, not just primary).
    const allMetadataProviders = getAllTrajectoryMetadataProviders();
    for (const p of allMetadataProviders) {
      p.prepareForReload(item.id, {
        invalidateMaterials: p === metadataProvider,
      });
    }
    const navStates = await Promise.all(
      allMetadataProviders.map((p) => p.saveNavigationState(item.id))
    );
    const navState = navStates.find((s) => s !== undefined);

    // Unload then load the (transformed) buffer
    await provider.unloadModel(item.id);

    options.signal.throwIfAborted();

    const model = await provider.loadModel(item.id, buffer, false, false);

    // Restore section materials, loaded sections, and navigation state
    if (model) {
      await metadataProvider.applyVisualsAndRestoreNavigation(
        item.id,
        navState
      );

      const loadedSections = getLoadedSectionsForMissionPath(
        dataState,
        item.id
      );
      if (loadedSections.length > 0) {
        await metadataProvider.updateLoadedSections(item.id, loadedSections);
      }
    }
  },

  unloadItem: async (
    _provider: TrimBimCapabilityProvider,
    _item: TrajectoryItem
  ) => {
    // Alignment unload is a no-op — the trajectory handler owns model lifecycle.
  },

  setLoadState: setItemLoadState<ProjectDataState>('trajectory'),

  onLoadError: () => {
    NotificationEventBus.emit({
      type: 'error',
      message: t('mobileMapping.projectData.trajectoryLoadFailed'),
    });
  },
});

/**
 * Replicates analyzed metadata and its source buffer from the primary provider
 * to all other TrajectoryMetadataProvider instances so every pane can resolve
 * alignment properties (e.g. isAlignment, sectionChainages).
 */
function syncMetadataToAllProviders(
  primary: TrajectoryMetadataProvider,
  allProviders: TrajectoryMetadataProvider[],
  itemId: string
): void {
  const metadata = primary.getById(itemId);
  const buffer = primary.getSourceBuffer(itemId);
  if (!metadata || !buffer) {
    return;
  }
  for (const provider of allProviders) {
    if (provider !== primary) {
      provider.storeAnalyzedMetadata(metadata, buffer);
    }
  }
}
